import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import errorMonitoringService from '../../../services/errorMonitoringService';

const RootCauseAnalysisPanel = ({ selectedError, rootCauseAnalysis, onAnalyzeError }) => {
  const [recentErrors, setRecentErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadRecentErrors();
  }, []);

  const loadRecentErrors = async () => {
    try {
      setLoading(true);
      const errors = await errorMonitoringService?.getAllErrors({ limit: 10, severity: 'critical' });
      setRecentErrors(errors || []);
    } catch (error) {
      console.error('Error loading recent errors:', error);
    } finally {
      setLoading(false);
    }
  };

  const getComplexityColor = (complexity) => {
    const colors = {
      high: 'text-red-600 bg-red-50',
      medium: 'text-yellow-600 bg-yellow-50',
      low: 'text-green-600 bg-green-50'
    };
    return colors?.[complexity] || 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Icon name="Search" className="w-5 h-5 text-purple-600" />
          AI-Powered Root Cause Analysis
        </h3>
        <p className="text-sm text-gray-600 mt-1">Natural language explanations powered by Anthropic Claude</p>
      </div>
      {/* Error Selection */}
      {!selectedError && (
        <div>
          <div className="text-sm font-medium text-gray-700 mb-3">Select Error for Analysis</div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {recentErrors?.map(error => (
              <div
                key={error?.id}
                className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors cursor-pointer"
                onClick={() => onAnalyzeError(error)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 mb-1">{error?.errorMessage}</div>
                    <div className="flex items-center gap-3 text-xs text-gray-600">
                      <span className="flex items-center gap-1">
                        <Icon name="AlertCircle" className="w-3 h-3" />
                        {error?.severity}
                      </span>
                      <span>•</span>
                      <span>{error?.affectedModule}</span>
                      <span>•</span>
                      <span>{new Date(error?.occurredAt)?.toLocaleString()}</span>
                    </div>
                  </div>
                  <Icon name="ChevronRight" className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Analysis Results */}
      {selectedError && rootCauseAnalysis && (
        <div className="space-y-6">
          {/* Selected Error */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <div className="font-medium text-gray-900">{selectedError?.errorMessage}</div>
                <div className="text-sm text-gray-600 mt-1">{selectedError?.errorCode}</div>
              </div>
              <Button
                onClick={() => onAnalyzeError(null)}
                variant="outline"
                size="sm"
              >
                <Icon name="X" className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Root Cause */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Target" className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-medium text-blue-900 mb-2">Root Cause</div>
                <p className="text-gray-700">{rootCauseAnalysis?.rootCause}</p>
              </div>
            </div>
          </div>

          {/* Contributing Factors */}
          {rootCauseAnalysis?.contributingFactors?.length > 0 && (
            <div>
              <div className="text-sm font-medium text-gray-700 mb-3">Contributing Factors</div>
              <ul className="space-y-2">
                {rootCauseAnalysis?.contributingFactors?.map((factor, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                    <Icon name="AlertTriangle" className="w-4 h-4 mt-0.5 text-yellow-600 flex-shrink-0" />
                    <span>{factor}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Resolution Steps */}
          {rootCauseAnalysis?.resolutionSteps?.length > 0 && (
            <div>
              <div className="text-sm font-medium text-gray-700 mb-3">Step-by-Step Resolution</div>
              <div className="space-y-3">
                {rootCauseAnalysis?.resolutionSteps?.map((step, idx) => (
                  <div key={idx} className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-semibold text-sm">
                      {step?.step}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 mb-1">{step?.action}</div>
                      <div className="text-sm text-gray-600">{step?.expectedOutcome}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Prevention Recommendations */}
          {rootCauseAnalysis?.preventionRecommendations?.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-sm font-medium text-green-900 mb-3 flex items-center gap-2">
                <Icon name="Shield" className="w-4 h-4" />
                Prevention Recommendations
              </div>
              <ul className="space-y-2">
                {rootCauseAnalysis?.preventionRecommendations?.map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                    <Icon name="CheckCircle" className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Metadata */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="flex items-center gap-4 text-sm text-gray-600">
              {rootCauseAnalysis?.estimatedResolutionTime && (
                <span className="flex items-center gap-1">
                  <Icon name="Clock" className="w-4 h-4" />
                  Est. Time: {rootCauseAnalysis?.estimatedResolutionTime}
                </span>
              )}
              {rootCauseAnalysis?.complexity && (
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  getComplexityColor(rootCauseAnalysis?.complexity)
                }`}>
                  {rootCauseAnalysis?.complexity?.toUpperCase()} COMPLEXITY
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Icon name="Sparkles" className="w-4 h-4 text-purple-600" />
              <span>Analyzed by Anthropic Claude</span>
            </div>
          </div>
        </div>
      )}
      {/* Empty State */}
      {!selectedError && recentErrors?.length === 0 && !loading && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Icon name="Search" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Errors Available</h3>
          <p className="text-gray-600">No recent errors found for analysis</p>
        </div>
      )}
    </div>
  );
};

export default RootCauseAnalysisPanel;
